/**
 * Markdown Parsing Utility
 * 
 * Parses markdown text to HTML for chat messages.
 * Hoisted outside component to avoid recreation on every render.
 * Based on React Best Practices: rendering-hoist-jsx
 * 
 * Performance: Avoids expensive regex operations on every render
 */

/**
 * Parse markdown text to HTML
 */
export const parseMarkdown = (text: string | null | undefined): string => {
  if (!text) return '';
  if (typeof text !== 'string') return String(text);

  let parsed = text;
  
  // Code blocks (multi-line)
  parsed = parsed.replace(
    /```([\s\S]*?)```/g, 
    '<code class="block bg-black/40 p-2 my-2 rounded border-l-[3px] border-success whitespace-pre-wrap overflow-x-auto">$1</code>'
  );
  
  // Inline code
  parsed = parsed.replace(
    /`([^`]+)`/g, 
    '<code class="bg-black/30 px-1.5 py-0.5 rounded text-[0.775rem] text-success border border-success/20">$1</code>'
  );
  
  // Bold (**text** or __text__)
  parsed = parsed.replace(/\*\*([^*\n]+?)\*\*/g, '<strong>$1</strong>');
  parsed = parsed.replace(/__([^_\n]+?)__/g, '<strong>$1</strong>');
  
  // Italic (*text* or _text_)
  parsed = parsed.replace(/(?<!\*)\*([^*\n]+?)\*(?!\*)/g, '<em>$1</em>');
  parsed = parsed.replace(/(?<!_)_([^_\n]+?)_(?!_)/g, '<em>$1</em>');
  
  // Process lines for lists and headings
  const lines = parsed.split('\n');
  const processedLines: string[] = [];
  let inList = false;
  let listType: 'ul' | 'ol' | null = null;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Unordered list items
    if (/^\s*[-*•]\s+(.+)/.test(line)) {
      const match = line.match(/^\s*[-*•]\s+(.+)/);
      if (!inList || listType !== 'ul') {
        if (inList && listType === 'ol') processedLines.push('</ol>');
        processedLines.push('<ul class="my-2 pl-5">');
        inList = true;
        listType = 'ul';
      }
      processedLines.push(`<li class="my-1 list-disc">${match![1]}</li>`);
    } 
    // Ordered list items
    else if (/^\s*\d+\.\s+(.+)/.test(line)) {
      const match = line.match(/^\s*\d+\.\s+(.+)/);
      if (!inList || listType !== 'ol') {
        if (inList && listType === 'ul') processedLines.push('</ul>');
        processedLines.push('<ol class="my-2 pl-5">');
        inList = true;
        listType = 'ol';
      }
      processedLines.push(`<li class="my-1 list-decimal">${match![1]}</li>`);
    } 
    // Headings
    else if (/^#{1,3}\s+(.+)/.test(line)) {
      if (inList) {
        processedLines.push(listType === 'ul' ? '</ul>' : '</ol>');
        inList = false;
        listType = null;
      }
      const match = line.match(/^(#{1,3})\s+(.+)/);
      const level = match![1].length;
      const sizeClass = level === 1 ? 'text-base' : level === 2 ? 'text-[0.9rem]' : 'text-[0.85rem]';
      processedLines.push(`<h${level + 2} class="font-semibold my-2 ${sizeClass}">${match![2]}</h${level + 2}>`);
    } 
    // Regular lines
    else {
      if (inList && line.trim() === '') {
        processedLines.push(listType === 'ul' ? '</ul>' : '</ol>');
        inList = false;
        listType = null;
      } else if (line.trim() !== '') {
        if (inList) {
          processedLines.push(listType === 'ul' ? '</ul>' : '</ol>');
          inList = false;
          listType = null;
        }
        processedLines.push(line + '<br>');
      } else if (!inList) {
        processedLines.push('<br>');
      }
    }
  }
  
  // Close any open lists
  if (inList) {
    processedLines.push(listType === 'ul' ? '</ul>' : '</ol>');
  }
  
  return processedLines.join('');
};

/**
 * Format message timestamp with "time ago" format
 */
export const formatMessageTime = (timestamp: string): string => {
  if (!timestamp) return '';
  
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  // For older messages, show the date
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const markdownUtils = {
  parseMarkdown,
  formatMessageTime
};

export default markdownUtils;
